/*
Copyright (c) 2024, HP Development Company, L.P. All rights reserved.
This software contains confidential and proprietary information of HP.
The user of this software agrees not to disclose, disseminate or copy
such Confidential Information and shall use the software only in accordance
with the terms of the license agreement the user entered into with HP.
*/

import { withAnalytics } from '@veneer/analytics';
import { Card, useMediaQuery } from '@veneer/core';
import React, { memo, useLayoutEffect, useRef, useState } from 'react';
import FallbackImageXL from '../../assets/images/secondary-card-fallback-xl.png';
import FallbackImageXS from '../../assets/images/secondary-card-fallback-xs.png';
import FallbackImageDarkXL from '../../assets/images/Secondary-card-fallback-dark-xl.png';
import FallbackImageDarkXS from '../../assets/images/Secondary-card-fallback-dark-xs.png';
import StatusList from '../status-list';
import Tooltip from '../tooltip';
import './styles.scss';
import { SecondaryDeviceCardProps } from './types';
import { breakpoints } from '../../base';
import useColorModeValue from '../../hooks/useColorModeValue';
import { generateID } from '../../base/helper';

/**
 * UI Toolkit : secondary device card
 * @public
 */

const TSecondaryDeviceCard: React.FC<SecondaryDeviceCardProps> = ({
  id = generateID('clientos-ui-toolkit-secondary-device-card'),
  deviceNickname,
  deviceModelName,
  deviceImage,
  onCardClick,
  statusList,
  loading,
  children
}) => {
  const deviceNicknameRef = useRef(null);
  const deviceModelNameRef = useRef(null);
  const deviceImageRef = useRef(null);
  const [isNicknameTruncated, setIsNicknameTruncated] = useState(false);
  const [isModelNameTruncated, setIsModelNameTruncated] = useState(false);
  const [isDeviceImageError, setIsDeviceImageError] = useState(false);
  const isDesktop = useMediaQuery(`(min-width: ${breakpoints.md})`);
  const fallbackImageXL = useColorModeValue(
    FallbackImageXL,
    FallbackImageDarkXL,
    FallbackImageDarkXL
  );
  const fallbackImageXS = useColorModeValue(
    FallbackImageXS,
    FallbackImageDarkXS,
    FallbackImageDarkXS
  );

  useLayoutEffect(() => {
    if (isDeviceImageError) {
      deviceImageRef.current.src = isDesktop
        ? fallbackImageXL
        : fallbackImageXS;
    }
  }, [isDeviceImageError, isDesktop, fallbackImageXL, fallbackImageXS]);

  const isTextTruncated = (element) => {
    if (!element) return false;
    return element.scrollHeight > element.clientHeight;
  };

  const isNicknameTruncatedCheck = (element) => {
    if (!element) return false;
    return element.scrollWidth > element.clientWidth;
  };

  useLayoutEffect(() => {
    setIsNicknameTruncated(isNicknameTruncatedCheck(deviceNicknameRef.current));
    setIsModelNameTruncated(isTextTruncated(deviceModelNameRef.current));
  }, [deviceNickname, deviceModelName]);

  return (
    <Card
      id={id}
      appearance="outlined"
      className={`clientos-ui-toolkit-secondary-device-card${loading ? ' clientos-ui-toolkit-secondary-device-card--loading' : ''}`}
      onClick={onCardClick}
      content={
        <div
          className="clientos-ui-toolkit-secondary-device-card__content-container"
          id={`${id}__content-container`}
        >
          <div
            className="clientos-ui-toolkit-secondary-device-card__summary"
            id={`${id}__summary`}
          >
            <div
              className="clientos-ui-toolkit-secondary-device-card__summary__info"
              id={`${id}__summary__info`}
            >
              {isNicknameTruncated ? (
                <Tooltip
                  content={deviceNickname}
                  id={`${id}__nickname__tooltip`}
                >
                  <h2
                    className={`clientos-ui-toolkit-secondary-device-card__device-name ${isDesktop ? 'subtitle-large-medium' : 'title-small--medium'}`}
                    ref={deviceNicknameRef}
                    id={`${id}__device-nickname`}
                  >
                    {deviceNickname}
                  </h2>
                </Tooltip>
              ) : (
                <h2
                  className={`clientos-ui-toolkit-secondary-device-card__device-name ${isDesktop ? 'subtitle-large-medium' : 'title-small--medium'}`}
                  ref={deviceNicknameRef}
                  id={`${id}__device-nickname`}
                >
                  {deviceNickname}
                </h2>
              )}
              {isModelNameTruncated ? (
                <Tooltip
                  content={deviceModelName}
                  id={`${id}__tooltip`}
                >
                  <h3
                    className="clientos-ui-toolkit-secondary-device-card__modal-name body-regular"
                    ref={deviceModelNameRef}
                    id={`${id}__device-model-name`}
                  >
                    {deviceModelName}
                  </h3>
                </Tooltip>
              ) : (
                <h3
                  className="clientos-ui-toolkit-secondary-device-card__modal-name body-regular"
                  ref={deviceModelNameRef}
                  id={`${id}__device-model-name`}
                >
                  {deviceModelName}
                </h3>
              )}
            </div>
            {statusList && (
              <div
                className="clientos-ui-toolkit-secondary-device-card__device-status"
                id={`${id}__device-status-list`}
              >
                <StatusList statusItemList={statusList} />
              </div>
            )}
            {children && (
              <div
                className="clientos-ui-toolkit-secondary-device-card__children"
                id={`${id}__children`}
              >
                {children}
              </div>
            )}
          </div>
          <div
            className={`clientos-ui-toolkit-secondary-device-card__device-image-container ${isDeviceImageError ? 'clientos-ui-toolkit-secondary-device-card__device-image-container--fallback' : ''}`}
            id={`${id}__device-image-container`}
          >
            <img
              className="clientos-ui-toolkit-secondary-device-card__device-image-container__device-image"
              src={deviceImage}
              alt="device-image"
              id={`${id}__device-image`}
              ref={deviceImageRef}
              onError={() => {
                setIsDeviceImageError(true);
              }}
            />
          </div>
        </div>
      }
    />
  );
};
export const SecondaryDeviceCardAnalytics = withAnalytics(TSecondaryDeviceCard);
export default memo(TSecondaryDeviceCard);
